import {
	Column,
	CreateDateColumn,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	PrimaryGeneratedColumn,
} from 'typeorm';
import { ClientLicenseMatchEntity } from './client-license-match.entity';

export type ClientLicenseMatchLogAction =
	| 'created'
	| 'confirmed'
	| 'rejected'
	| 'reverted'
	| 'confidence_updated'
	// PD-16593 — the reviewer decisions the Recent Changes list shows.
	| 'skipped'
	| 'manual_matched'
	| 'unmatched'
	| 'undone';

/**
 * The action values in column order. The enum column is widened by the
 * instance migration AddRecentChangeActionsToMatchLog (database sync never
 * alters an existing column), so the two lists must stay in step.
 */
export const CLIENT_LICENSE_MATCH_LOG_ACTIONS: ReadonlyArray<ClientLicenseMatchLogAction> =
	[
		'created',
		'confirmed',
		'rejected',
		'reverted',
		'confidence_updated',
		'skipped',
		'manual_matched',
		'unmatched',
		'undone',
	];

/**
 * Audit log for client_license_match status and confidence changes.
 * Does NOT extend BaseEntity — lightweight log table with minimal columns.
 *
 * PD-16593: also the record behind the Recent Changes list — one row per
 * reviewer decision (confirm, reject, skip, manual match, unmatch) and one per
 * undo. A decision that changes the customer's license keeps the license
 * values before and after it (`license_before` / `license_after`), which is
 * what an undo restores and what its guard compares against. Rows are never
 * erased: an undo adds its own row and points the original at it.
 */
@Index('idx_clml_match', ['fkClientLicenseMatchId'])
@Index('idx_clml_created', ['createdDate'])
@Index('idx_clml_user_created', ['userId', 'createdDate'])
@Entity('client_license_match_log')
export class ClientLicenseMatchLogEntity {
	@PrimaryGeneratedColumn({ name: '_id', unsigned: true })
	public id!: number;

	@Column('int', {
		name: 'fk_client_license_match__id',
		unsigned: true,
	})
	public fkClientLicenseMatchId!: number;

	@Column('enum', {
		name: 'action',
		enum: [
			'created',
			'confirmed',
			'rejected',
			'reverted',
			'confidence_updated',
			'skipped',
			'manual_matched',
			'unmatched',
			'undone',
		],
	})
	public action!: ClientLicenseMatchLogAction;

	@Column('varchar', { name: 'old_status', nullable: true, length: 50 })
	public oldStatus!: string | null;

	@Column('varchar', { name: 'new_status', nullable: true, length: 50 })
	public newStatus!: string | null;

	@Column('decimal', {
		name: 'old_confidence',
		nullable: true,
		precision: 5,
		scale: 2,
	})
	public oldConfidence!: string | null;

	@Column('decimal', {
		name: 'new_confidence',
		nullable: true,
		precision: 5,
		scale: 2,
	})
	public newConfidence!: string | null;

	@Column('int', { name: 'user_id', nullable: true, unsigned: true })
	public userId!: number | null;

	@Column('varchar', { name: 'note', nullable: true, length: 1000 })
	public note!: string | null;

	/** PD-16593: the match's client, so the list reads without the match row. */
	@Column('int', { name: 'fk_client__id', nullable: true, unsigned: true })
	public fkClientId!: number | null;

	/** PD-16593: the customer license the decision touched, if any. */
	@Column('int', {
		name: 'fk_client_license__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientLicenseId!: number | null;

	/**
	 * PD-16593: made by the system rather than a person — a linked-license
	 * cascade. Confirms by the Workd Automation user are recognised by user.
	 */
	@Column('tinyint', {
		name: 'is_automatic',
		width: 1,
		default: 0,
		transformer: {
			to: (value: boolean | null | undefined): number =>
				value === true ? 1 : 0,
			from: (value: number | null): boolean => value === 1,
		},
	})
	public isAutomatic!: boolean;

	/** PD-16593: the license's values just before the decision (null: none). */
	@Column('json', { name: 'license_before', nullable: true })
	public licenseBefore!: Record<string, unknown> | null;

	/** PD-16593: the license's values the decision left behind. */
	@Column('json', { name: 'license_after', nullable: true })
	public licenseAfter!: Record<string, unknown> | null;

	/**
	 * PD-16593: what else the decision did and an undo needs — the matches it
	 * superseded, a license it created, the match row's prior state.
	 */
	@Column('json', { name: 'details', nullable: true })
	public details!: Record<string, unknown> | null;

	/** PD-16593: on an 'undone' row, the decision it undid. */
	@Column('int', { name: 'undo_of_log__id', nullable: true, unsigned: true })
	public undoOfLogId!: number | null;

	/** PD-16593: on a decision, the 'undone' row that reversed it. */
	@Column('int', { name: 'undone_by_log__id', nullable: true, unsigned: true })
	public undoneByLogId!: number | null;

	@CreateDateColumn({
		name: '_created_date',
		nullable: false,
		precision: 0,
		default: () => 'CURRENT_TIMESTAMP',
	})
	public createdDate!: Date;

	@ManyToOne(() => ClientLicenseMatchEntity, (match) => match.logs, {
		onDelete: 'CASCADE',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_client_license_match__id', referencedColumnName: 'id' },
	])
	public fkClientLicenseMatch!: ClientLicenseMatchEntity;
}
